import React from 'react';
import styled, { css } from 'styled-components';
import Link from 'next/link';

import { getDispensaryHomePageUrl } from 'shared/helpers/dispensaries';
import { Typography } from 'src/typography';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useInstoreEnrollment } from 'src/hooks/use-instore-enrollment';
import { DoctorOutlineIcon } from 'src/assets/doctor-outline-icon';
import { MedCardOutline } from 'src/assets/med-card-outline';
import { StepHeader, StepFooter, AnchorButton } from '../shared-components';

export const MedSuccess: React.FC = () => {
  const enrollmentStore = useInstoreEnrollment();
  const phone = enrollmentStore.userPhone || 'the phone number provided';
  const { hasValidMedCard } = enrollmentStore;

  const { dispensary } = useDispensary();
  const hasEmbeddedUrl = !!dispensary?.embeddedMenuUrl;
  const homePageUrl = getDispensaryHomePageUrl(dispensary, hasEmbeddedUrl);

  if (hasValidMedCard) {
    return (
      <>
        <StepHeader title="You're all set!" />
        <SuccessContainer data-testid='med-success-page'>
          <MedCardOutline />
          <Typography.Heading tag='h2' size='large'>
            You already have an active Med ID on file
          </Typography.Heading>
          <Typography.Body tag='p' size='medium'>
            You can begin shopping!
          </Typography.Body>
        </SuccessContainer>

        <StepFooter>
          <AnchorButton as={Link} href={homePageUrl}>
            Start Shopping
          </AnchorButton>
        </StepFooter>
      </>
    );
  }

  return (
    <>
      <StepHeader title="You're all set!" />
      <SuccessContainer data-testid='med-success-page'>
        <DoctorOutlineIcon />
        <Typography.Heading tag='h2' size='large'>
          A clinician will call you shortly at {phone}* for your free consult.
        </Typography.Heading>
        <Typography.Body tag='p' size='medium'>
          You can begin shopping while you wait!
        </Typography.Body>
      </SuccessContainer>

      <StepFooter>
        <AnchorButton as={Link} href={homePageUrl}>
          Start Shopping
        </AnchorButton>
        <Footnote>*International visitors must enable roaming. Message and Data Rates May Apply.</Footnote>
      </StepFooter>
    </>
  );
};

const SuccessContainer = styled.div`
  ${({ theme: { colors, spaces } }) => css`
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    padding: ${spaces[6]};
    height: 100%;
    gap: ${spaces[4]};

    p {
      color: ${colors.grey[30]};
    }
  `}
`;

const Footnote = styled(Typography.Body).attrs({
  tag: 'p',
  size: 'small',
})`
  ${({ theme: { colors } }) => css`
    color: ${colors.grey[45]};
    max-width: 80%;
    margin: 0 auto;
    font-size: 12px;
    line-height: 14px;
  `}
`;
